import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Briefcase,
  MapPin,
  CheckCircle2,
  Users,
  Sparkles,
  Heart,
  Calendar,
  Gift,
  Camera,
  ArrowRight,
  Star,
  Instagram,
  Facebook,
  Linkedin,
  BookOpen
} from "lucide-react";
import { SiX, SiWhatsapp } from "react-icons/si";

// Use public assets that are committed to the repository
const heroImage = "/og-image.jpg";

export default function InfluencerJobPosting() {
  const [shareUrl, setShareUrl] = useState("https://snugglecrafters.com/jobs/influencer");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setShareUrl(window.location.href);
    }

    document.title = "Influencer Program | SnuggleCrafters - Join Our Creator Community";
    
    const updateOrCreateMeta = (selector: string, attrType: 'name' | 'property', attrValue: string, content: string) => {
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrType, attrValue);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateOrCreateMeta('meta[name="description"]', 'name', 'description', 
      'Join the SnuggleCrafters Influencer Program! Create content for the #1 AI Storytelling Platform for Families. Part-time, remote opportunity. No minimum follower count required.');
    
    updateOrCreateMeta('meta[property="og:title"]', 'property', 'og:title', 
      'SnuggleCrafters Influencer Program - Join Our Creator Community');
    
    updateOrCreateMeta('meta[property="og:description"]', 'property', 'og:description', 
      'Become a creator for the #1 AI Storytelling Platform for Families. Get exclusive access to brand resources and create magical content for families worldwide.');
    
    updateOrCreateMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    
    updateOrCreateMeta('meta[property="og:url"]', 'property', 'og:url', 
      'https://snugglecrafters.com/jobs/influencer');
    
    updateOrCreateMeta('meta[property="og:image"]', 'property', 'og:image', 
      'https://snugglecrafters.com/og-image.jpg');
    
    updateOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    
    updateOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', 
      'SnuggleCrafters Influencer Program - Join Our Creator Community');
    
    updateOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', 
      'Become a creator for the #1 AI Storytelling Platform for Families. Part-time, remote opportunity. No minimum follower count required.');
    
    updateOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', 
      'https://snugglecrafters.com/og-image.jpg');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://snugglecrafters.com/jobs/influencer');
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-yellow-50/30 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        <div className="text-center mb-8">
          <Link href="/" data-testid="link-home">
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 bg-clip-text text-transparent cursor-pointer">
              SnuggleCrafters
            </span>
          </Link>
        </div>

        <Link href="/snugglecrafters-influencer-program" data-testid="link-hero-image">
          <div className="mb-8 rounded-2xl overflow-hidden shadow-2xl cursor-pointer hover:shadow-3xl transition-shadow duration-300">
            <img 
              src={heroImage} 
              alt="SnuggleCrafters Influencer Program - Family-Friendly AI Storytelling Platform" 
              className="w-full h-auto object-cover hover:scale-[1.02] transition-transform duration-300"
              data-testid="img-hero"
            />
          </div>
        </Link>

        <Card className="border-0 shadow-xl mb-8">
          <CardContent className="p-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300" data-testid="badge-part-time">
                <Briefcase className="w-3 h-3 mr-1" />
                Part-Time
              </Badge>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" data-testid="badge-remote">
                <MapPin className="w-3 h-3 mr-1" />
                Remote
              </Badge>
              <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" data-testid="badge-flexible">
                <Sparkles className="w-3 h-3 mr-1" />
                Flexible
              </Badge>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white" data-testid="text-job-title">
              SnuggleCrafters Influencer Program
            </h1>
            
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6" data-testid="text-job-subtitle">
              Become a Creator for the #1 AI Storytelling Platform for Families
            </p>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed" data-testid="text-job-description">
              <strong>SnuggleCrafters.com</strong> is the AI-powered platform that helps families create personalized bedtime stories, 
              illustrated books, and magical adventures in seconds. We're expanding our creator community and inviting 
              influencers who love storytelling, creativity, parenting content, education, or family-friendly media.
            </p>

            <div className="flex flex-wrap gap-3 mt-6">
              <Link href="/snugglecrafters-influencer-program" data-testid="link-apply-now">
                <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="button-apply-now">
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/" data-testid="link-visit-site">
                <Button size="lg" variant="outline" data-testid="button-learn-more">
                  Visit SnuggleCrafters
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <Card className="border-0 shadow-lg" data-testid="card-what-you-do">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
                <Camera className="w-5 h-5 text-purple-600" />
                What You'll Do
              </h2>
              <ul className="space-y-3">
                {[
                  "Create authentic content showcasing SnuggleCrafters (short videos, reviews, demo stories, behind-the-scenes)",
                  "Share unique creator discount codes with your audience",
                  "Promote SnuggleCrafters during key campaigns (holidays, back-to-school, special releases)",
                  "Provide feedback directly to our product team",
                  "Represent the brand with positivity, creativity, and family-friendly values"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg" data-testid="card-what-you-get">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
                <Gift className="w-5 h-5 text-pink-600" />
                What You'll Get
              </h2>
              <ul className="space-y-3">
                {[
                  "Official Influencer Portal with brand assets, media kits, and content ideas",
                  "Unique referral codes to share with your audience",
                  "Early access to new SnuggleCrafters features and story styles",
                  "Exclusive invites to creator webinars, giveaways, and previews",
                  "Opportunities for paid collaborations and long-term partnerships",
                  "Support from our internal product and marketing team"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-lg mb-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20" data-testid="card-looking-for">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Users className="w-5 h-5 text-purple-600" />
              Who We're Looking For
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">You're a great fit if you:</p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                "Create content for parents, kids, families, teachers, or book lovers",
                "Are active on TikTok, Instagram, YouTube, Facebook, or Pinterest",
                "Have strong on-camera presence OR create high-quality voiceover/animation content",
                "Maintain an encouraging, safe, family-friendly online presence",
                "Love experimenting with AI, creativity, and storytelling",
                "Want to grow your brand alongside an exciting platform"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                  <Heart className="w-4 h-4 text-pink-500 flex-shrink-0 mt-1" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-sm font-medium text-purple-700 dark:text-purple-300 flex items-center gap-2" data-testid="text-no-minimum">
                <Sparkles className="w-4 h-4" />
                No minimum follower count required – quality of content matters most.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg mb-8 border-l-4 border-l-yellow-400" data-testid="card-first-steps">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <BookOpen className="w-5 h-5 text-yellow-600" />
              First Steps Before Applying
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 italic">
              Complete the following steps before submitting your application.
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">A. Create Account & Test App</h3>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 ml-4">
                  <li>• Sign up at <a href="https://snugglecrafters.com" className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer" data-testid="link-signup">snugglecrafters.com</a></li>
                  <li>• Generate minimum 3 stories</li>
                  <li>• Explore all features thoroughly</li>
                  <li>• Document genuine impressions</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">B. Follow Social Media Accounts</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="gap-1" data-testid="badge-instagram">
                    <Instagram className="w-3 h-3" /> @SnuggleCrafters
                  </Badge>
                  <Badge variant="outline" className="gap-1" data-testid="badge-instagram-teddy">
                    <Instagram className="w-3 h-3" /> @Teddy_SnuggleCrafters
                  </Badge>
                  <Badge variant="outline" className="gap-1" data-testid="badge-facebook">
                    <Facebook className="w-3 h-3" /> @SnuggleCrafters
                  </Badge>
                  <Badge variant="outline" className="gap-1" data-testid="badge-whatsapp">
                    <SiWhatsapp className="w-3 h-3" /> SnuggleCrafters.com
                  </Badge>
                  <Badge variant="outline" className="gap-1" data-testid="badge-linkedin">
                    <Linkedin className="w-3 h-3" /> @SnuggleCrafters
                  </Badge>
                  <Badge variant="outline" className="gap-1" data-testid="badge-x">
                    <SiX className="w-3 h-3" /> @SnuggleCrafters
                  </Badge>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">C. Post Initial Content</h3>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 ml-4">
                  <li>• Create and post on each of your platforms</li>
                  <li>• Use <span className="text-purple-600 font-medium">#SnuggleCraftersMagic</span></li>
                  <li>• Tag @SnuggleCrafters</li>
                  <li>• See Hashtag Guide</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">D. Send Confirmation</h3>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 ml-4">
                  <li>• Email: <a href="mailto:stories@snugglecrafters.com" className="text-purple-600 hover:underline" data-testid="link-email">stories@snugglecrafters.com</a></li>
                  <li>• Subject: <span className="font-medium">"Influencer Onboarding Complete - [Your Name]"</span></li>
                  <li>• Include links to posts and handles</li>
                  <li>• Share first impressions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg mb-8" data-testid="card-how-to-apply">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Calendar className="w-5 h-5 text-blue-600" />
              How to Apply
            </h2>
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 p-6 rounded-lg">
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center flex-shrink-0">1</span>
                  <span className="text-gray-700 dark:text-gray-300">Apply directly through the SnuggleCrafters app under your profile page</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center flex-shrink-0">2</span>
                  <span className="text-gray-700 dark:text-gray-300">Our Chief Product Officer will review your application</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center flex-shrink-0">3</span>
                  <span className="text-gray-700 dark:text-gray-300">Approved creators receive onboarding instructions and full creator portal access</span>
                </li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg mb-8 bg-gray-50 dark:bg-gray-800/50" data-testid="card-about">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Sparkles className="w-5 h-5 text-purple-600" />
              About SnuggleCrafters
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed" data-testid="text-about">
              SnuggleCrafters.com is a family-focused AI storytelling platform that generates personalized illustrated books, 
              bedtime stories, and creative adventures at the tap of a button. Loved by parents, teachers, and kids around the world.
            </p>
          </CardContent>
        </Card>

        <div className="text-center mb-8">
          <Link href="/snugglecrafters-influencer-program" data-testid="link-apply-bottom">
            <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-6" data-testid="button-apply-bottom">
              Apply to Join the Influencer Program
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>

        <Separator className="mb-8" />

        <div className="text-center mb-8">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Share this opportunity</p>
          <div className="flex justify-center gap-4">
            <a 
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              data-testid="link-share-linkedin"
              aria-label="Share on LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a 
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Join the SnuggleCrafters Influencer Program! Create content for the #1 AI Storytelling Platform for Families')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
              data-testid="link-share-twitter"
              aria-label="Share on X (Twitter)"
            >
              <SiX className="w-5 h-5" />
            </a>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              data-testid="link-share-facebook"
              aria-label="Share on Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>

        <footer className="text-center text-sm text-gray-500 dark:text-gray-400 pb-8">
          <p data-testid="text-copyright">© {new Date().getFullYear()} SnuggleCrafters. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link href="/terms" className="hover:text-purple-600" data-testid="link-terms">Terms of Service</Link>
            <Link href="/privacy" className="hover:text-purple-600" data-testid="link-privacy">Privacy Policy</Link>
            <Link href="/contact" className="hover:text-purple-600" data-testid="link-contact">Contact</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
